/**
 * FRAGRIFY THEME - MAIN JAVASCRIPT
 * Optimized for speed and performance
 */

// ========================================
// UTILITY FUNCTIONS
// ========================================

/**
 * Debounce function to limit function calls
 */
function debounce(func, wait, immediate) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      timeout = null;
      if (!immediate) func(...args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func(...args);
  };
}

/**
 * Throttle function to limit function calls
 */
function throttle(func, limit) {
  let inThrottle;
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

/**
 * Check if element is in viewport
 */
function isInViewport(element) {
  const rect = element.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
}

/**
 * Format price from cents to currency
 */
function formatPrice(priceInCents, currency = 'USD') {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency
  }).format(priceInCents / 100);
}

/**
 * Safe query selector with error handling
 */
function safeQuerySelector(selector, context = document) {
  try {
    return context.querySelector(selector);
  } catch (error) {
    return null;
  }
}

/**
 * Safe query selector all with error handling
 */
function safeQuerySelectorAll(selector, context = document) {
  try {
    return context.querySelectorAll(selector);
  } catch (error) {
    return [];
  }
}

// ========================================
// HEADER SEARCH FUNCTIONALITY
// ========================================


// ========================================
// SEARCH PAGE FUNCTIONALITY
// ========================================

class SearchPage {
  constructor() {
    this.filtersSidebar = safeQuerySelector('.search-filters');
    this.filtersToggle = safeQuerySelector('#filters-toggle');
    this.closeFilters = safeQuerySelector('#close-filters');
    this.clearFilters = safeQuerySelector('#clear-filters');
    this.resultsGrid = safeQuerySelector('#search-results-grid');
    this.sortSelect = safeQuerySelector('#sort-select');
    
    this.init();
  }
  
  init() {
    if (!this.filtersSidebar || !this.resultsGrid) return;
    
    this.setupFilters();
    this.setupSorting();
    this.setupMobileFilters();
  }
  
  setupFilters() {
    // Filter checkboxes
    const filterInputs = safeQuerySelectorAll('input[type="checkbox"]', this.filtersSidebar);
    filterInputs.forEach(input => {
      input.addEventListener('change', debounce(() => {
        this.applyFilters();
      }, 150));
    });
    
    // Price range inputs
    const priceInputs = safeQuerySelectorAll('.search-filter__price-input', this.filtersSidebar);
    priceInputs.forEach(input => {
      input.addEventListener('input', debounce(() => {
        this.applyFilters();
      }, 300));
    });
    
    // Clear filters button
    if (this.clearFilters) {
      this.clearFilters.addEventListener('click', () => {
        this.clearAllFilters();
      });
    }
  }
  
  setupSorting() {
    if (!this.sortSelect || !this.resultsGrid) return;
    
    this.sortSelect.addEventListener('change', (e) => {
      this.sortResults(e.target.value);
    });
  }
  
  setupMobileFilters() {
    if (!this.filtersToggle || !this.filtersSidebar) return;
    
    this.filtersToggle.addEventListener('click', () => {
      this.filtersSidebar.classList.add('active');
      document.body.style.overflow = 'hidden';
    });
    
    if (this.closeFilters) {
      this.closeFilters.addEventListener('click', () => {
        this.closeFiltersSidebar();
      });
    }
    
    // Close filters when clicking outside
    document.addEventListener('click', (e) => {
      if (this.filtersSidebar.classList.contains('active') && 
          !this.filtersSidebar.contains(e.target) && 
          !this.filtersToggle.contains(e.target)) {
        this.closeFiltersSidebar();
      }
    });
  }
  
  closeFiltersSidebar() {
    this.filtersSidebar.classList.remove('active');
    document.body.style.overflow = '';
  }
  
  applyFilters() {
    const productCards = safeQuerySelectorAll('.search-result-card', this.resultsGrid);
    if (productCards.length === 0) return;
    
    productCards.forEach(card => {
      let show = true;
      
      // Type filter
      const typeCheckboxes = safeQuerySelectorAll('input[name="type"]:checked', this.filtersSidebar);
      if (typeCheckboxes.length > 0) {
        let typeMatch = false;
        typeCheckboxes.forEach(checkbox => {
          if (card.dataset.type === checkbox.value) {
            typeMatch = true;
          }
        });
        if (!typeMatch) show = false;
      }
      
      // Price filter
      const priceMin = safeQuerySelector('#price-min');
      const priceMax = safeQuerySelector('#price-max');
      if (priceMin && priceMax && card.dataset.price) {
        const productPrice = parseFloat(card.dataset.price) / 100;
        const minPrice = parseFloat(priceMin.value) || 0;
        const maxPrice = parseFloat(priceMax.value) || Infinity;
        
        if (productPrice < minPrice || productPrice > maxPrice) {
          show = false;
        }
      }
      
      // Availability filter
      const availabilityCheckboxes = safeQuerySelectorAll('input[name="availability"]:checked', this.filtersSidebar);
      if (availabilityCheckboxes.length > 0) {
        let availabilityMatch = false;
        availabilityCheckboxes.forEach(checkbox => {
          if (checkbox.value === 'in-stock' && card.dataset.available === 'true') {
            availabilityMatch = true;
          }
          if (checkbox.value === 'on-sale' && card.querySelector('.price-compare')) {
            availabilityMatch = true;
          }
        });
        if (!availabilityMatch) show = false;
      }
      
      // Show/hide card with animation
      if (show) {
        card.style.display = 'block';
        card.style.animation = 'fadeIn 0.3s ease';
      } else {
        card.style.display = 'none';
      }
    });
    
    this.updateResultsCount();
  }
  
  sortResults(sortBy) {
    const productCards = Array.from(safeQuerySelectorAll('.search-result-card', this.resultsGrid));
    if (productCards.length === 0) return;
    
    productCards.sort((a, b) => {
      switch (sortBy) {
        case 'price-asc':
          return (parseFloat(a.dataset.price) || 0) - (parseFloat(b.dataset.price) || 0);
        case 'price-desc':
          return (parseFloat(b.dataset.price) || 0) - (parseFloat(a.dataset.price) || 0);
        case 'title-asc':
          return (a.dataset.title || '').localeCompare(b.dataset.title || '');
        case 'title-desc':
          return (b.dataset.title || '').localeCompare(a.dataset.title || '');
        default:
          return 0;
      }
    });
    
    // Re-append sorted cards
    productCards.forEach(card => {
      this.resultsGrid.appendChild(card);
    });
  }
  
  clearAllFilters() {
    // Clear all checkboxes
    const checkboxes = safeQuerySelectorAll('input[type="checkbox"]', this.filtersSidebar);
    checkboxes.forEach(checkbox => {
      checkbox.checked = false;
    });
    
    // Clear price inputs
    const priceInputs = safeQuerySelectorAll('.search-filter__price-input', this.filtersSidebar);
    priceInputs.forEach(input => {
      input.value = '';
    });
    
    // Show all cards
    const productCards = safeQuerySelectorAll('.search-result-card', this.resultsGrid);
    productCards.forEach(card => {
      card.style.display = 'block';
    });
    
    // Reset sort
    if (this.sortSelect) {
      this.sortSelect.value = 'relevance';
    }
    
    this.updateResultsCount();
  }
  
  updateResultsCount() {
    const visibleCards = safeQuerySelectorAll('.search-result-card[style*="block"], .search-result-card:not([style*="none"])', this.resultsGrid);
    const count = visibleCards.length;
    
    const resultsCount = safeQuerySelector('.search-results-count');
    if (resultsCount) {
      const originalText = resultsCount.textContent;
      const newText = originalText.replace(/\d+/, count);
      resultsCount.textContent = newText;
    }
  }
}

// ========================================
// PERFORMANCE OPTIMIZATIONS
// ========================================

class PerformanceOptimizer {
  constructor() {
    this.init();
  }
  
  init() {
    this.setupLazyLoading();
    this.setupIntersectionObserver();
    this.optimizeImages();
    this.setupPreloading();
  }
  
  setupLazyLoading() {
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            if (img.dataset.src) {
              img.src = img.dataset.src;
              img.removeAttribute('data-src');
              observer.unobserve(img);
            }
          }
        });
      });
      
      const lazyImages = safeQuerySelectorAll('img[data-src]');
      lazyImages.forEach(img => imageObserver.observe(img));
    }
  }
  
  setupIntersectionObserver() {
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('fade-in');
          }
        });
      }, {
        threshold: 0.1,
        rootMargin: '50px'
      });
      
      const elements = safeQuerySelectorAll('.search-result-card, .card');
      elements.forEach(el => observer.observe(el));
    }
  }
  
  optimizeImages() {
    const images = safeQuerySelectorAll('img');
    images.forEach(img => {
      // Add loading="lazy" if not present
      if (!img.hasAttribute('loading')) {
        img.setAttribute('loading', 'lazy');
      }
      
      // Add decoding="async" for better performance
      if (!img.hasAttribute('decoding')) {
        img.setAttribute('decoding', 'async');
      }
    });
  }
  
  setupPreloading() {
    // Shopify assets are CDN-hosted; no manual preloading needed
  }
}

// ========================================
// INITIALIZATION
// ========================================

class ThemeInitializer {
  constructor() {
    this.init();
  }
  
  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initializeComponents());
    } else {
      this.initializeComponents();
    }
  }
  
  initializeComponents() {
    // Initialize components based on current page
    const currentPath = window.location.pathname;
    
    // Always initialize performance optimizations
    new PerformanceOptimizer();
    
    
    // Initialize search page functionality
    if (currentPath.includes('/search')) {
      new SearchPage();
    }
    
    // Initialize other components as needed
    this.initializeOtherComponents();
  }
  
  initializeOtherComponents() {
    // Add other component initializations here
    // e.g., cart functionality, product galleries, etc.
  }
}

// ========================================
// ERROR HANDLING
// ========================================

window.addEventListener('error', (event) => {
  console.error('Theme error:', event.error);
  // You can add error reporting here
});

window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled promise rejection:', event.reason);
  // You can add error reporting here
});

// ========================================
// START THEME
// ========================================

// Initialize theme when script loads
new ThemeInitializer();

// Export for potential external use
window.FragrifyTheme = {
  SearchPage,
  PerformanceOptimizer,
  utils: {
    debounce,
    throttle,
    isInViewport,
    formatPrice,
    safeQuerySelector,
    safeQuerySelectorAll
  }
};
